/**
 * HeinzSight brand marks.
 *
 * The wordmark is a raster asset in /public/brand, and it is NOT surface-agnostic: the
 * supplied artwork is red-and-navy on a light ground, so the navy half of "HeinzSight"
 * disappears into a dark surface. A white monochrome cut exists for exactly that case.
 *
 * Picking between them is therefore a real decision, and one a caller can silently get
 * wrong — so it is made HERE, from the active theme, rather than pushed onto every call
 * site. (The previous logo took a `variant` prop and the login page passed the wrong one:
 * it asked for the dark-on-light cut while sitting on a dark card.) A caller that genuinely
 * needs to override — the sidebar, which can be light while the app theme is dark — passes
 * `surface` explicitly.
 */
import { useTheme } from '@lognai/synapse-design-system-v2';

const BRAND_RED = '#FF0000';
const BRAND_NAVY = '#012D69';

interface LogoProps {
  className?: string;
  /**
   * The surface the mark sits ON. Defaults to the active theme, which is right everywhere
   * except the sidebar, whose light/dark mode is independent of the app theme.
   */
  surface?: 'dark' | 'light';
}

export function Logo({ className, surface }: LogoProps) {
  const { theme } = useTheme();
  const resolvedSurface = surface ?? (theme === 'light' ? 'light' : 'dark');

  // White cut on dark surfaces; full-colour artwork on light ones.
  const source =
    resolvedSurface === 'dark' ? '/brand/heinzsight-white.png' : '/brand/heinzsight-light.png';

  return (
    <img
      src={source}
      className={className}
      alt="HeinzSight — Supply Chain Management Portal"
    />
  );
}

/**
 * Icon-only mark for the collapsed sidebar and the favicon.
 *
 * Deliberately not the wordmark: at 32×32 a 2.5:1 wordmark is an illegible smudge. Drawn
 * rather than cropped from the artwork, because the source is a raster script face and a
 * cropped letterform goes soft the moment it is scaled down.
 *
 * The white badge is what lets one mark work on a dark sidebar, a light sidebar and a
 * browser tab alike — the navy would otherwise vanish on dark.
 */
export function LogoMark({ className }: LogoProps) {
  return (
    <svg
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="HeinzSight"
    >
      <rect x="0" y="0" width="48" height="48" rx="10" fill="#ffffff" />
      <text
        x="24"
        y="33"
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize="26"
        fontStyle="italic"
        fontWeight="700"
      >
        <tspan fill={BRAND_RED}>H</tspan>
        <tspan fill={BRAND_NAVY}>S</tspan>
      </text>
    </svg>
  );
}
